import { notEmpty } from "@product/scmp-sdk";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeSecondaryContentItemContent$key } from "~/queries/__generated__/homeSecondaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  CoverImage,
  Headline,
  LinkHighlightArea,
  Section,
  SectionContainer,
  SponsorTagContainer,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  SubHeadline,
  Summary,
} from "./styles";

export type Props = {
  reference: homeSecondaryContentItemContent$key;
  variantProps?: ContentItemHomeSecondaryVariantProps;
} & ContentItemRenderVariantProps;

export type ContentItemHomeSecondaryVariantProps = {
  skipHighlight?: boolean;
  withBookmark?: boolean;
  withComment?: boolean;
  withImage?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withSubHeadline?: boolean;
  withSummary?: boolean;
};

export const ContentItemHomeSecondary = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      onClick,
      provider,
      reference: reference_,
      variantProps = {
        withBookmark: false,
        withComment: false,
        withImage: false,
        withLiveTag: false,
        withSection: false,
        withSubHeadline: false,
        withSummary: false,
      },
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homeSecondaryContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    const isSponsorArticle = notEmpty(provider.sponsorTag?.());

    return (
      <Container className={className} ref={reference}>
        {variantProps?.withSection && (
          <SectionContainer>
            <Section>{provider.sections()}</Section>
          </SectionContainer>
        )}

        <StyledHeadlineEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <LinkHighlightArea>
            <Headline>
              {provider.headline({
                preferSocialHeadline: true,
                skipHighlight: variantProps?.skipHighlight,
              })}
              {variantProps?.withLiveTag && provider.liveTag()}
            </Headline>

            {variantProps?.withSummary && (
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
            )}
            {variantProps?.withSubHeadline && !isSponsorArticle && (
              <SubHeadline>{provider.subHeadline()}</SubHeadline>
            )}
          </LinkHighlightArea>
        </StyledHeadlineEntityLink>

        {isSponsorArticle ? (
          <SponsorTagContainer>{provider.sponsorTag?.({ variant: "home" })}</SponsorTagContainer>
        ) : (
          <ActionBar>
            {provider.actionBar({
              isBookmarkVisible: variantProps?.withBookmark ?? false,
              isCommentsVisible: variantProps?.withComment ?? false,
            })}
          </ActionBar>
        )}
        {variantProps?.withImage && (
          <StyledCoverEntityLink
            $isSponsorArticle={isSponsorArticle}
            reference={content}
            onClick={() => onClick?.(content.entityId)}
          >
            <CoverImage>
              {provider.coverImage({
                responsiveVariants: {
                  desktopUp: "size540x360",
                  homeDesktopUp: "size540x360",
                  mobileUp: "size540x360",
                  tabletUp: "size540x360",
                },
              })}
            </CoverImage>
          </StyledCoverEntityLink>
        )}
      </Container>
    );
  },
);

ContentItemHomeSecondary.displayName = "ContentItemHomeSecondary";
